/**
 * @file        ui.jsx
 * @module      modules/kyb/manager/console
 * @description Shared bits of the review console: labels, SLA text, relative
 *              times, initials, the toast and the modal frame — the same
 *              wording and markup as the shared HTML design.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export const CHECK_KEYS = ["business", "individual", "association", "authority"];
export const CHECK_NAMES = { business: "Business", individual: "Identity", association: "Link to business", authority: "Authority" };
/** check status → [class, icon, label] */
export const CS = {
  verified: ["ok", "✓", "Passed"],
  review: ["rev", "!", "Needs review"],
  nomatch: ["bad", "✕", "Failed"],
  declined: ["bad", "✕", "Failed"],
  progress: ["prog", "", "Ongoing"],
  pending: ["", "", "Pending"],
};
export const STATUS = {
  review: ["review", "Needs review"],
  awaiting: ["awaiting", "Awaiting Customer"],
  auto: ["auto", "Approved · auto"],
  manual: ["manual", "Approved · manual"],
  rejected: ["rejected", "Rejected"],
  progress: ["progress", "Customer verifying"],
};
export const TABS = [
  ["review", "Needs review"],
  ["mine", "Assigned to me"],
  ["awaiting", "Awaiting Customer"],
  ["auto", "Auto-approved"],
  ["manual", "Approved by team"],
  ["rejected", "Rejected"],
  ["progress", "Customer verifying"],
  ["all", "All sign-ups"],
];

const H = 3600e3;

export const initials = (n) =>
  String(n || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export function fmtWhen(t) {
  if (!t) return "—";
  const d = (Date.now() - new Date(t).getTime()) / H;
  if (d < 1) return `${Math.max(1, Math.round(d * 60))} min ago`;
  if (d < 24) return `${Math.round(d)}h ago`;
  return `${Math.round(d / 24)}d ago`;
}

export const fmtDur = (h) => {
  const m = Math.max(1, Math.round(h * 60));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

/** SLA cell text and bar, like the design: one working day for cases needing review. */
export function sla(status, slaDueAt) {
  if (status === "awaiting") return { cls: "paused", txt: "Paused · waiting on Customer", pct: 0, bar: false };
  if (status !== "review" || !slaDueAt) return { cls: "none", txt: "—", pct: 0, bar: false };
  const left = (new Date(slaDueAt).getTime() - Date.now()) / H;
  const pct = Math.min(100, Math.max(0, (1 - left / 24) * 100));
  if (left < 0) return { cls: "over", txt: `Overdue by ${fmtDur(-left)}`, pct: 100, bar: true };
  return { cls: left < 4 ? "soon" : "", txt: `Due in ${fmtDur(left)}`, pct, bar: true };
}

export function Sla({ status, dueAt }) {
  const s = sla(status, dueAt);
  return (
    <div className={`sla ${s.cls}`}>
      <span>{s.txt}</span>
      {s.bar ? (
        <i>
          <b style={{ width: `${s.pct}%` }} />
        </i>
      ) : null}
    </div>
  );
}

export const Chip = ({ status }) => {
  const st = STATUS[status] || ["progress", status];
  return <span className={`chip ${st[0]}`}>{st[1]}</span>;
};

export function fmtSize(bytes) {
  if (!bytes) return "";
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

// ------------------------------------------------------------------ toast --

const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }) {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);
  const toast = useCallback((m) => {
    setMsg(m);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(null), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      {msg ? (
        <div className="toast" role="status" aria-live="polite">
          {msg}
        </div>
      ) : null}
    </ToastCtx.Provider>
  );
}

// ------------------------------------------------------------ row actions --

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 4.3c.4-1.7 3-1.7 3.4 0a1.7 1.7 0 0 0 2.6 1.1c1.5-.9 3.3.8 2.4 2.4a1.7 1.7 0 0 0 1 2.5c1.8.4 1.8 3 0 3.4a1.7 1.7 0 0 0-1 2.6c.9 1.5-.9 3.3-2.4 2.4a1.7 1.7 0 0 0-2.6 1c-.4 1.8-3 1.8-3.4 0a1.7 1.7 0 0 0-2.6-1c-1.5.9-3.3-.9-2.4-2.4a1.7 1.7 0 0 0-1-2.6c-1.8-.4-1.8-3 0-3.4a1.7 1.7 0 0 0 1-2.5c-.9-1.6.9-3.3 2.4-2.4a1.7 1.7 0 0 0 2.6-1.1z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * A row's actions behind one gear, as on the admin KYB Managers list.
 *
 * @param {{ items: Array<{key, label, onClick, danger?, disabled?}>, label: string, busy?: boolean }} props
 */
export function RowActions({ items, label, busy }) {
  // The table scrolls sideways, so an absolutely positioned menu would be
  // clipped by that container: place it against the viewport instead, from the
  // gear's own position, and close it if anything scrolls underneath.
  const [at, setAt] = useState(null);
  const box = useRef(null);
  const open = !!at;

  const toggle = () => {
    if (open) return setAt(null);
    const r = box.current.getBoundingClientRect();
    setAt({ top: Math.round(r.bottom + 6), right: Math.round(window.innerWidth - r.right) });
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setAt(null);
    const onAway = (e) => !box.current?.contains(e.target) && close();
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onAway);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  return (
    <span className="rowmenu" ref={box}>
      <button type="button" className="gearbtn" aria-label={label} aria-expanded={open} disabled={busy} onClick={toggle}>
        <GearIcon />
      </button>
      {open ? (
        <div className="menu rowmenu-pop" role="menu" style={{ top: at.top, right: at.right }}>
          {items.map((it) => (
            <button
              key={it.key}
              role="menuitem"
              className={it.danger ? "danger" : ""}
              disabled={it.disabled}
              onClick={() => {
                setAt(null);
                it.onClick();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}

// ------------------------------------------------------------------ modal --

export function Modal({ title, sub, children, footer, onClose, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="kybcModalTitle">
        <header>
          <h2 id="kybcModalTitle">{title}</h2>
          {sub ? <p>{sub}</p> : null}
        </header>
        <div className="mbody">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </div>
    </div>
  );
}
